import React from 'react';
import {toast} from 'react-toastify';
import {Button} from 'components/forms/Buttons';
import {ButtonProps} from '@mui/material';
import ButtonDialog from 'components/forms/ButtonDialog';
import {useMutate} from 'hooks/useFetchData';
import {RecordType} from 'components/record/RecordContext';
import {useResourceContext} from 'components/record/ResourceContext';

interface ActionButtonProps extends Omit<ButtonProps, 'onClick' | 'onError'> {
  method: string;
  variables?: Record<string, unknown>;
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
  label: string;
  confirmMessage?: string;
  confirmTitle?: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  method,
  variables = {},
  onSuccess,
  onError,
  label,
  confirmMessage,
  confirmTitle = 'Confirm Action',
  ...buttonProps
}) => {
  const {resource} = useResourceContext();
  const [mutate, mutationResult] = useMutate<{item: RecordType}>(method, resource, {
    variables
  });

  const loading = mutationResult?.loading ?? false;

  const handleAction = async () => {
    if (typeof mutate === 'function') {
      try {
        const {data} = await mutate();
        if (onSuccess) {
          onSuccess(data);
        }
      } catch (error) {
        if (onError) {
          onError(error as Error);
        } else {
          toast.error(error instanceof Error ? error.message : 'An error occurred');
        }
      }
    }
  };

  if (confirmMessage) {
    return (
      <ButtonDialog
        buttonLabel={label}
        dialogTitle={confirmTitle}
        primaryButton={
          <Button variant="contained" onClick={handleAction} disabled={!mutate || loading}>
            {loading ? 'Loading...' : 'Confirm'}
          </Button>
        }
        {...buttonProps}
      >
        {confirmMessage}
      </ButtonDialog>
    );
  }

  return (
    <Button {...buttonProps} onClick={handleAction} disabled={!mutate || loading || buttonProps.disabled}>
      {label}
    </Button>
  );
};

export default ActionButton;
